import { useCallback, useEffect, useState } from 'react'

export type ThemePreference = 'system' | 'light' | 'dark'

// Only an explicit light/dark pick is stored; "system" is the absence of a
// key. (Not 'theme': the old hook wrote its default to that key on every
// first visit, so it can't tell a choice from a default.)
const STORAGE_KEY = 'theme-choice'
const DARK_QUERY = '(prefers-color-scheme: dark)'

function readStored(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored
  } catch {
    // Storage inaccessible (private browsing, disabled) -- follow the system.
  }
  return 'system'
}

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches
}

// Keep the layout's inline <head> script in step with this: it applies the
// same rule before first paint so there is no flash.
function applyTheme(preference: ThemePreference) {
  const dark = preference === 'system' ? systemPrefersDark() : preference === 'dark'
  document.documentElement.classList.toggle('dark', dark)
}

export function useTheme() {
  // 'system' on the server and on the first client render, so hydration
  // matches; the stored choice is read in an effect.
  const [preference, setPreference] = useState<ThemePreference>('system')

  useEffect(() => {
    setPreference(readStored())
  }, [])

  // While following the system, track it live (sunset auto-switch).
  useEffect(() => {
    if (preference !== 'system' || typeof window.matchMedia !== 'function') return
    const query = window.matchMedia(DARK_QUERY)
    const onChange = () => applyTheme('system')
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [preference])

  const choose = useCallback((next: ThemePreference) => {
    applyTheme(next)
    try {
      if (next === 'system') localStorage.removeItem(STORAGE_KEY)
      else localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Nothing to persist to -- the choice still applies for this session.
    }
    setPreference(next)
  }, [])

  return { preference, setPreference: choose }
}
